import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Calendar, CalendarDays, Clock, Plus, Edit, Video, Check, Move } from 'lucide-react';
import { EVENT_COLORS, EVENT_TYPES, VISIBILITY_OPTIONS, REMINDER_OPTIONS, formatTime } from './constants';

export function DayDetailModal({ open, onClose, selectedDay, getEventsForDay, openEditModal, openCreateModal }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-blue-600" />
            {selectedDay?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
          {selectedDay && getEventsForDay(selectedDay).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Aucun événement ce jour</p>
            </div>
          ) : (
            selectedDay && getEventsForDay(selectedDay).map((event) => {
              const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
              return (
                <div key={event.id} className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => { onClose(false); openEditModal(event); }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.light }}>
                      <Clock className="h-5 w-5" style={{ color: config.text }} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{event.title}</div>
                      <div className="text-sm text-gray-500">{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</div>
                    </div>
                    <Badge style={{ backgroundColor: config.bg }} className="text-white text-xs">{config.label}</Badge>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Fermer</Button>
          <Button onClick={() => { onClose(false); openCreateModal(selectedDay); }}>
            <Plus className="h-4 w-4 mr-1" />Ajouter un événement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EventFormModal({ open, onClose, selectedEvent, eventForm, setEventForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedEvent ? (
              <><Edit className="h-5 w-5 text-blue-600" /> Modifier l&apos;événement</>
            ) : (
              <><Plus className="h-5 w-5 text-green-600" /> Nouvel événement</>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Titre *</Label>
            <Input value={eventForm.title} onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
              placeholder="Titre de l'événement" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({...eventForm, event_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />{type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visibilité</Label>
              <Select value={eventForm.visibility} onValueChange={(v) => setEventForm({...eventForm, visibility: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2"><opt.icon className="h-4 w-4" />{opt.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Début *</Label>
              <Input type="datetime-local" value={eventForm.start_datetime}
                onChange={(e) => setEventForm({...eventForm, start_datetime: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Fin *</Label>
              <Input type="datetime-local" value={eventForm.end_datetime}
                onChange={(e) => setEventForm({...eventForm, end_datetime: e.target.value})} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
              placeholder="Description de l'événement" rows={3} className="mt-1" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Checkbox id="is_online" checked={eventForm.is_online}
              onCheckedChange={(checked) => setEventForm({...eventForm, is_online: checked})} />
            <Label htmlFor="is_online" className="cursor-pointer flex items-center gap-2">
              <Video className="h-4 w-4 text-sky-600" />Événement en ligne
            </Label>
          </div>
          {eventForm.is_online ? (
            <div>
              <Label>Lien de la réunion</Label>
              <Input value={eventForm.online_link} onChange={(e) => setEventForm({...eventForm, online_link: e.target.value})}
                placeholder="https://meet.google.com/..." className="mt-1" />
            </div>
          ) : (
            <div>
              <Label>Lieu</Label>
              <Input value={eventForm.location} onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                placeholder="Adresse ou lieu de l'événement" className="mt-1" />
            </div>
          )}
          <div>
            <Label>Rappel</Label>
            <Select value={String(eventForm.reminder_minutes)}
              onValueChange={(v) => setEventForm({...eventForm, reminder_minutes: parseInt(v)})}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Annuler</Button>
          <Button onClick={onSubmit} className="bg-blue-600 hover:bg-blue-700">
            {selectedEvent ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MoveConfirmModal({ open, onClose, moveData, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="h-5 w-5 text-blue-600" />Déplacer l&apos;événement
          </DialogTitle>
        </DialogHeader>
        {moveData.event && moveData.newDate && (
          <div className="py-4 space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800">{moveData.event.title}</h4>
              <p className="text-sm text-blue-600 mt-1">
                {EVENT_COLORS[moveData.event.event_type]?.label || 'Événement'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Date actuelle</p>
                <p className="font-medium">
                  {new Date(moveData.event.start_datetime).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-sm text-gray-600">{formatTime(moveData.event.start_datetime)} - {formatTime(moveData.event.end_datetime)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-xs text-green-600 mb-1">Nouvelle date</p>
                <p className="font-medium text-green-800">
                  {moveData.newDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-sm text-green-600">{formatTime(moveData.event.start_datetime)} - {formatTime(moveData.event.end_datetime)}</p>
                <p className="text-xs text-green-500 mt-1">(horaires conservés)</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center">L&apos;événement sera déplacé en conservant les mêmes horaires.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700">
            <Check className="h-4 w-4 mr-2" />Confirmer le déplacement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
